import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Link,
  Button,
} from "@chakra-ui/react";
import { PhoneIcon, EmailIcon } from "@chakra-ui/icons";

const SAFFRON = "#E07B1A";
const SAFFRON_DARK = "#854F0B";
const SAFFRON_PALE = "#FAEEDA";

export default function Contact() {
  return (
    <Box maxW="900px" mx="auto" p={{ base: 4, md: 8 }}>
      {/* ── Hero ── */}
      <VStack spacing={3} textAlign="center" mb={10}>
        <Box fontSize="40px" lineHeight="1">
          📍
        </Box>
        <Heading fontSize={{ base: "2xl", md: "3xl" }} color={SAFFRON}>
          Contact Information
        </Heading>
        <Box w="48px" h="2px" bg={SAFFRON} borderRadius="full" opacity={0.6} />
      </VStack>

      {/* ── Address card ── */}
      <Box
        p={5}
        mb={6}
        borderRadius="16px"
        bg={SAFFRON_PALE}
        border="1px solid"
        borderColor="orange.200"
      >
        <HStack align="start" spacing={3}>
          <Text fontSize="24px">🛕</Text>
          <Box>
            <Text fontWeight="700" color={SAFFRON_DARK} mb={1}>
              Temple Address
            </Text>
            <Text fontSize="sm" color="gray.700" lineHeight="1.8">
              Sri Annapurna Sametha Visweswara Swamy Alayam,
              <br />
              D.No: 1-122/1, Thokada, Rajanagaram Mandalam,
              <br />
              East Godavari District, Andhra Pradesh - 533296
            </Text>
          </Box>
        </HStack>
      </Box>

      {/* ── Chairman + Email cards ── */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={10}>
        <Box
          p={5}
          borderRadius="16px"
          bg="white"
          border="1px solid"
          borderColor="orange.100"
          boxShadow="0 4px 14px rgba(133,79,11,0.08)"
        >
          <Text fontWeight="700" color={SAFFRON_DARK} mb={2}>
            Chairman
          </Text>
          <Text fontSize="sm" color="gray.700" mb={3}>
            Shri Vegisetti Chakrarao
          </Text>
          <Link href="tel:+919493575676" _hover={{ textDecoration: "none" }}>
            <HStack
              spacing={2}
              bg={SAFFRON}
              color="white"
              px={4}
              py={2}
              borderRadius="full"
              w="fit-content"
              fontWeight="600"
              fontSize="sm"
              transition="all 0.15s ease"
              _hover={{ bg: SAFFRON_DARK, transform: "translateY(-1px)" }}
            >
              <PhoneIcon boxSize={3} />
              <Text>+91 9493575676</Text>
            </HStack>
          </Link>
        </Box>

        <Box
          p={5}
          borderRadius="16px"
          bg="white"
          border="1px solid"
          borderColor="orange.100"
          boxShadow="0 4px 14px rgba(133,79,11,0.08)"
        >
          <Text fontWeight="700" color={SAFFRON_DARK} mb={2}>
            Email
          </Text>
          <Text fontSize="sm" color="gray.700" mb={3}>
            For queries, donations &amp; general enquiries
          </Text>
          <Link
            href="mailto:sasvsathokada@gmail.com"
            _hover={{ textDecoration: "none" }}
          >
            <HStack
              spacing={2}
              bg="white"
              border="1.5px solid"
              borderColor={SAFFRON}
              color={SAFFRON_DARK}
              px={4}
              py={2}
              borderRadius="full"
              w="fit-content"
              fontWeight="600"
              fontSize="sm"
              transition="all 0.15s ease"
              _hover={{ bg: "orange.50" }}
            >
              <EmailIcon boxSize={3} />
              <Text>sasvsathokada@gmail.com</Text>
            </HStack>
          </Link>
        </Box>
      </SimpleGrid>

      {/* ── Map ── */}
      <Heading fontSize="lg" color={SAFFRON_DARK} mb={4}>
        Temple Location Map
      </Heading>
      <Box
        borderRadius="16px"
        overflow="hidden"
        border="1px solid"
        borderColor="orange.200"
        boxShadow="0 6px 20px rgba(133,79,11,0.1)"
      >
        <iframe
          title="Temple Location"
          src="https://maps.google.com/maps?q=17.0091925,81.9234107&z=16&output=embed"
          width="100%"
          height="320"
          style={{ border: 0, display: "block" }}
          loading="lazy"
          allowFullScreen=""
        />
      </Box>

      <Link
        href="https://www.google.com/maps/place/Sivalayam/@17.0091925,81.9208358,17z/data=!4m14!1m7!3m6!1s0x3a37990040c32a81:0xcb27795e440bc030!2sSivalayam!8m2!3d17.0091925!4d81.9234107!16s%2Fg%2F11w2gs99b7!3m5!1s0x3a37990040c32a81:0xcb27795e440bc030!8m2!3d17.0091925!4d81.9234107!16s%2Fg%2F11w2gs99b7"
        isExternal
        _hover={{ textDecoration: "none" }}
      >
        <HStack
          justify="center"
          spacing={2}
          mt={3}
          bg={SAFFRON_PALE}
          border="1px solid"
          borderColor="orange.200"
          color={SAFFRON_DARK}
          py={2.5}
          borderRadius="full"
          fontWeight="600"
          fontSize="sm"
          transition="all 0.15s ease"
          _hover={{ bg: "orange.100" }}
        >
          <Text>📍 Get Directions on Google Maps</Text>
        </HStack>
      </Link>

      {/* ── Quick call CTA ── */}
      <VStack
        mt={10}
        spacing={3}
        textAlign="center"
        p={6}
        borderRadius="20px"
        bg={`linear-gradient(135deg, ${SAFFRON}, ${SAFFRON_DARK})`}
        color="white"
      >
        <Text fontSize="md" fontWeight="600">
          🙏 We're happy to answer any questions
        </Text>
        <Button
          as="a"
          href="tel:+919493575676"
          size="lg"
          bg="white"
          color={SAFFRON_DARK}
          fontWeight="700"
          borderRadius="full"
          px={8}
          _hover={{ bg: "orange.50", transform: "translateY(-2px)" }}
          transition="all 0.18s ease"
        >
          📞 Call the Temple
        </Button>
      </VStack>
    </Box>
  );
}
